// React
import { Fragment, useState } from "react"

// Components
import { BaseTable, Card, Checkbox, DateInput, ErrorText, HeaderMenu, Input, Loading, Select, TabSection, TableHeader, TableSection, Textarea } from "components"
import { DetailSectionReturn } from "../../ReturnItems/components"
import { Code } from "./Code"
import { DetailRefund } from "./DetailRefund"
import { DetailReturn } from "./DetailReturn"

// Contexts
import { DataContext, RefundDetailContext } from "../contexts"

// Form
import { Controller, useFieldArray, useFormContext } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"

// Types
import type { DetailType } from "../../ReturnRecord/types"

// Utils
import { convertNumber, useAccounts, useEmployee } from "utils"
import { statusList } from "../../ReturnRecord/utils"
import { type FormType, pageConfig } from "../utils"
import { default_option } from "pages/Purchase/utils"

export function FormSection(params: {
  return_id?:number | null
  approve_by?: number | null
  approve_status?: number
  invoice: {
    sales_order_id?: number
    trx_code?: string
    total_payment?:number
  }
  return: {
    address: string
    customer_id: number
    customer_name: string
    detailreturn: DetailType[]
    invoice_id: number
    location_id: number
    location_name: string
    return_status: number
    transaction_date: string
    trx_code: string
  }
  trx_code?: string
}): JSX.Element {
  // Vars
  const invoice = params.invoice
  
  const returnData = params.return
  const detailreturn = returnData.detailreturn
  const location = {
    id: returnData.location_id,
    name: returnData.location_name
  }
  const customer = {
    id: returnData.customer_id,
    name: returnData.customer_name
  }

  // Form
  const { control, formState: { errors }, getValues } = useFormContext<FormType>()
  const { fields, append, update } = useFieldArray({
    control,
    name: "refund_return_detail"
  })

  const detailRefund = getValues()

  const sum: {
    subtotal: number
    tax: number
  } = detailRefund.refund_return_detail.reduce((acc, item) => {
    // Vars
    const total: number = item.price_refund * (item.qty_return_item ?? 0)
    const totalAfterDiscount: number = total - (total *item.discount / 100)
    const tax = totalAfterDiscount * item.tax_rate / 100

    return {
      subtotal: acc.subtotal + totalAfterDiscount,
      tax: acc.tax + tax
    }
  }, {
    subtotal: 0,
    tax: 0
  })
  const subtotal = sum.subtotal
  const tax = sum.tax
  const total = subtotal + tax


  return (
    <>
      <HeaderMenu title="DATA ENTRY | REFUND MONEY">
        {params.trx_code ?? (
          <Code
            jenis={pageConfig.type_refund}
            returncode={returnData.trx_code}
          />
        )}
      </HeaderMenu>

      <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3 items-end">
        <Select
          isDisabled
          label="CUSTOMER"
          placeholder="Select Customer"
          options={[
            {
              label: customer.name,
              value: customer.id.toString()
            }
          ]}
          value={{
            label: customer.name,
            value: customer.id.toString()
          }}
        />

        <section className="lg:justify-self-end mb-3">
          <Checkbox readOnly value>
            TAX-INCLUSIVE
          </Checkbox>
        </section>
      </section>

      <Card>
        <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <Textarea
              disabled
              label="SHIPPED"
              value={returnData.address}
            />

            <DateInput
              disabled
              label="RETURN DATE"
              selected={moment(returnData.transaction_date).toDate()}
              onChange={() => {}}
            />

            <Select
              isDisabled
              label="LOCATION"
              placeholder="Select Location"
              options={[
                {
                  label: location.name,
                  value: location.id.toString()
                }
              ]}
              value={{
                label: location.name,
                value: location.id.toString()
              }}
            />

            <section className="flex items-end">
              <Input
                disabled
                className="grow"
                label="LINK"
                value={returnData?.trx_code}
              />

              {returnData && (
                <DetailSectionReturn
                  return_id={params.return_id??0}
                />
              )}
            </section>
          </section>

          <section className="flex flex-col gap-3">
            <Controller
              name="transaction_date"
              control={control}
              render={({ field, fieldState }) => (
                <DateInput
                  label="DATE"
                  disabled={field.disabled}
                  error={fieldState.error?.message}
                  selected={moment(field.value).toDate()}
                  ref={field.ref}
                  onChange={(res) => {
                    if (res) {
                      field.onChange(moment(res).format("YYYY-MM-DD"))
                    }
                  }}
                />
              )}
            />

            <Controller
              name="referensi"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label="REFERENCE"
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="memo"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label="MEMO"
                  error={fieldState.error?.message}
                />
              )}
            />

            <Coa />

            <Input
              disabled
              className="grow"
              label="TOTAL PAYMENT"
              value={convertNumber(invoice?.total_payment ?? 0).intoCurrency}
            />
          </section>
        </Card.Body>
      </Card>

      <section>
        <BaseTable>
          <TableHeader>
            <tr>
              <th>QTY RETURN</th>
              <th>QTY REFUND</th>
              <th>BACK RETURN</th>
              <th>UNIT</th>
              <th>ITEM CODE</th>
              <th>DESCRIPTION</th>
              <th>PRICE</th>
              <th>DISC (%)</th>
              <th>TOTAL</th>
              <th>TAX</th>
              <th>ACTION</th>
            </tr>
          </TableHeader>

          <tbody>
            {!fields.length ? (
              <tr>
                <td colSpan={11}>
                  <Loading errorText="No data available" />
                </td>
              </tr>
            ) : fields.map((item, key) => (
              <Fragment key={key}>
                <RefundDetailContext.Provider
                  value={{
                    item,
                    onUpdate: (updatedItem) => {
                      update(key, updatedItem)
                    }
                  }}
                >
                  <DetailRefund />
                </RefundDetailContext.Provider>
              </Fragment>
            ))}
          </tbody>
        </BaseTable>

        <ErrorMessage
          errors={errors}
          name="refund_return_detail"
          render={({ message }) => <ErrorText text={message} />}
        />
      </section>

      <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
        <section className="flex flex-col gap-3">
          <TableSection>
            <TableSection.Row
              label="RETURN STATUS"
              value={statusList.find(item => item.value === returnData.return_status.toString())?.label ?? "-"}
            />

            <TableSection.Row
              label="APPROVE STATUS"
              value={default_option.find(item => item.value === params.approve_status?.toString())?.label ?? "-"}
            />

            <ApprovedBy approve_by={params.approve_by ?? undefined} />
          </TableSection>
        </section>

        <section className="flex flex-col lg:items-end gap-3">
          <div className="w-full min-w-[100px] md:max-w-[400px] text-right">
            <TableSection>
              <TableSection.Row
                label="SUBTOTAL"
                value={convertNumber(subtotal).intoCurrency}
              />

              <TableSection.Row
                label="TAX"
                value={convertNumber(tax).intoCurrency}
              />

              <TableSection.Row
                className="font-bold"
                label="TOTAL REFUND"
                value={convertNumber(total).intoCurrency}
              />

              <TableSection.Row
                className="font-bold"
                label="BALANCE"
                value={convertNumber(total).intoCurrency}
              />
            </TableSection>
          </div>
        </section>
      </section>

      <DataContext.Provider
        value={{
          datainvoice:invoice,
          detailreturn: detailreturn,
          onAddDetail: (value) => {
            // Vars
            const selectedDetail = getValues("refund_return_detail").find(item => item.product_id === value.product_id)

            if (selectedDetail) {
              return toast.error("Data has already added into detail table")
            }
            if(value.back_returned === 0){
              return toast.error("Data has already refunded")
            }

            append({
              qty_return_item: 0,
              qty_unit: 0,
              discount: value.discount_return,
              price_refund: value.price_return,
              product_barcode: value.product_barcode,
              product_id: value.product_id,
              product_name: value.product_name,
              qty_back_returned: value.back_returned,
              quantity_unit_sell_convention: value.quantity_unit_sell_convention,
              tax_id: value.tax_id,
              tax_name:value.tax_name,
              tax_rate: value.tax_rate,
              total: value.total_return,
              total_tax_collect: value.total_tax_return,
              unit_convention_name: value.unit_convention_name,
              unit_sell_measure_name: value.unit_sell_measure_name
            })
          }
        }}
      >
        <Tab />
      </DataContext.Provider>
    </>
  )
}

function ApprovedBy(params: {
  approve_by?: number
}): JSX.Element {
  // Hooks
  const { dataEmployee } = useEmployee()

  return (
    <TableSection.Row
      label="APPROVED BY"
      value={dataEmployee.find(item => item.value === params.approve_by?.toString())?.label ?? "-"}
    />
  )
}

function Coa(): JSX.Element {
  // Hooks
  const { data, isLoading } = useAccounts()

  // Form
  const { control } = useFormContext<FormType>()

  return (
    <Controller
      name="coa_cheque_id"
      control={control}
      render={({ field, fieldState }) => (
        <Select
          label="ACCOUNT CHEQUE"
          placeholder="Select Account"
          error={fieldState.error?.message}
          isDisabled={field.disabled}
          isLoading={isLoading}
          options={data}
          ref={field.ref}
          value={data.find(item => item.value === field.value) ?? null}
          onChange={item => {
            if (item) {
              field.onChange(item.value)
            }
          }}
        />
      )}
    />
  )
}

function Tab(): JSX.Element {
  // Vars
  const tabList: string[] = ["DETAIL RETURN"]

  // Hooks
  const [currentTab, setCurrentTab] = useState<number>(0)

  return (
    <section>
      <TabSection
        activeIndex={currentTab}
        list={tabList}
        onChangeTab={setCurrentTab}
      />

      {Boolean(currentTab === 0) && <DetailReturn />}
    </section>
  )
}